import i18n from 'i18next';

import {
  reportCommonTranslations,
  reportSalesTranslations,
  reportPaymentsTranslations,
  reportProductsTranslations,
  reportCancellationTranslations,
} from './index';

/**
 * Register Reports Domain Translations
 *
 * Registers all reports domain translations (reportCommon, reportSales, reportPayments, reportCancellation)
 * directly into i18next instance. This makes the domain self-contained
 * and independent from /public/locales/ files for its specific needs.
 *
 * This function should be called once when the domain is loaded,
 * typically in the domain's main index or at app initialization.
 *
 * @example
 * // In App initialization or domain entry point
 * import { registerReportsTranslations } from 'domains/reports';
 * registerReportsTranslations();
 */
export const registerReportsTranslations = (): void => {
  // Register reportCommon namespace (domain-specific common translations)
  Object.entries(reportCommonTranslations).forEach(([language, resources]) => {
    i18n.addResourceBundle(language, 'reportCommon', resources, true, true);
  });

  // Register reportSales namespace
  Object.entries(reportSalesTranslations).forEach(([language, resources]) => {
    i18n.addResourceBundle(language, 'reportSales', resources, true, true);
  });

  // Register reportPayments namespace
  Object.entries(reportPaymentsTranslations).forEach(([language, resources]) => {
    i18n.addResourceBundle(language, 'reportPayments', resources, true, true);
  });

  // Register reportProducts namespace
  Object.entries(reportProductsTranslations).forEach(([language, resources]) => {
    i18n.addResourceBundle(language, 'reportProducts', resources, true, true);
  });

  // Register reportCancellation namespace
  Object.entries(reportCancellationTranslations).forEach(([language, resources]) => {
    i18n.addResourceBundle(language, 'reportCancellation', resources, true, true);
  });
};

/**
 * Check if Reports Translations are loaded
 *
 * Utility function to verify if reports domain translations
 * have been registered in i18next.
 *
 * @param language - Language to check (defaults to current i18n language)
 * @returns {boolean} True if translations are loaded
 */
export const areReportsTranslationsLoaded = (language?: string): boolean => {
  const lng = language || i18n.language;
  return (
    i18n.hasResourceBundle(lng, 'reportCommon') &&
    i18n.hasResourceBundle(lng, 'reportSales') &&
    i18n.hasResourceBundle(lng, 'reportPayments') &&
    i18n.hasResourceBundle(lng, 'reportProducts') &&
    i18n.hasResourceBundle(lng, 'reportCancellation')
  );
};
